import Vue from "vue";
import Vuex from "vuex";
import { getModule } from "vuex-module-decorators";
import createPersistedState from "vuex-persistedstate"; // storeの情報保存先を変更するプラグイン

import { SystemSetting } from "@/commons/services/SystemSettings";

import common, { CommonState } from "@/store/Common";
import loginUser, { LoginUserState } from "@/store/LoginUser";
import fonts, { FontsState } from "@/store/Fonts";
import notification, { NotificationState } from "@/store/Notification";
import tabIndexes, { TabIndexesState } from "@/store/TabIndexes";
import systemSetting from "@/store/SystemSetting";
import editAccount, { EditAccountState } from "@/store/EditAccount";
import editOtherOfficeConnection, { EditOtherOfficeConnectionState } from "@/store/EditOtherOfficeConnection";
import needRefreshes, { NeedRefreshesState } from "@/store/NeedRefreshes";
import nationalHolidays, { NationalHolidaysState } from "@/store/NationalHolidays";
import regions, { RegionsState } from "@/store/Regions";
import screenInputTemporary, { ScreenInputTemporaryState } from "@/store/ScreenInputTemporary";
import itemListFilter, { ItemListFilterState } from "@/store/ItemListFilter";

import mobileMessageHistory, { MobileMessageHistoryState } from "@/store/MobileMessageHistory";
import mobileMessageRegister, { MobileMessageRegisterState } from "@/store/MobileMessageRegister";
import mobileMessageReplyDetail, { MobileMessageReplyDetailState } from "@/store/MobileMessageReplyDetail";
import mobileFamilyReceiveMessage, { MobileFamilyReceiveMessageState } from "@/store/MobileFamilyReceiveMessage";
import mobileFamilyReplyReceiveMessage, { MobileFamilyReplyReceiveMessageState } from "@/store/MobileFamilyReplyReceiveMessage";
import mobileOtherUserReceiveMessage, { MobileOtherUserReceiveMessageState } from "@/store/MobileOtherUserReceiveMessage";
import mobileOtherUserReplyReceiveMessage, { MobileOtherUserReplyReceiveMessageState } from "@/store/MobileOtherUserReplyReceiveMessage";
import mobileSendSafetyConfirmToOtherUser, { MobileSendSafetyConfirmToOtherUserState } from "@/store/MobileSendSafetyConfirmToOtherUser";
import mobileSendSafetyConfirmDetailToOtherUser, { MobileSendSafetyConfirmDetailToOtherUserState } from "@/store/MobileSendSafetyConfirmDetailToOtherUser";
import mobileSafetyConfirmOtherUserTemplate, { MobileSafetyConfirmOtherUserTemplateState } from "@/store/MobileSafetyConfirmOtherUserTemplate";
import mobileSafetyConfirmRegister, { MobileSafetyConfirmRegisterState } from "@/store/MobileSafetyConfirmRegister";

Vue.use(Vuex);

/**
 * ### RootState
 */
export interface RootState {
  common: CommonState;
  loginUser: LoginUserState;
  fonts: FontsState;
  notification: NotificationState;
  tabIndexes: TabIndexesState;
  systemSetting: SystemSetting;
  editAccount: EditAccountState;
  editOtherOfficeConnection: EditOtherOfficeConnectionState;
  needRefreshes: NeedRefreshesState;
  nationalHolidays: NationalHolidaysState;
  regions: RegionsState;
  screenInputTemporary: ScreenInputTemporaryState;
  itemListFilter: ItemListFilterState;

  mobileMessageHistory: MobileMessageHistoryState;
  mobileMessageRegister: MobileMessageRegisterState;
  mobileMessageReplyDetail: MobileMessageReplyDetailState;
  mobileFamilyReceiveMessage: MobileFamilyReceiveMessageState;
  mobileFamilyReplyReceiveMessage: MobileFamilyReplyReceiveMessageState;
  mobileOtherUserReceiveMessage: MobileOtherUserReceiveMessageState;
  mobileOtherUserReplyReceiveMessage: MobileOtherUserReplyReceiveMessageState;
  mobileSendSafetyConfirmToOtherUser: MobileSendSafetyConfirmToOtherUserState;
  mobileSendSafetyConfirmDetailToOtherUser: MobileSendSafetyConfirmDetailToOtherUserState;
  mobileSafetyConfirmOtherUserTemplate: MobileSafetyConfirmOtherUserTemplateState;
  mobileSafetyConfirmRegister: MobileSafetyConfirmRegisterState;
}

/**
 * ### RootStoreを作成する
 */
const store = new Vuex.Store<any>({
  state: {},
  modules: {
    common,
    loginUser,
    fonts,
    notification,
    tabIndexes,
    systemSetting,
    editAccount,
    editOtherOfficeConnection,
    needRefreshes,
    nationalHolidays,
    regions,
    screenInputTemporary,
    itemListFilter,

    mobileMessageHistory,
    mobileMessageRegister,
    mobileMessageReplyDetail,
    mobileFamilyReceiveMessage,
    mobileFamilyReplyReceiveMessage,
    mobileOtherUserReceiveMessage,
    mobileOtherUserReplyReceiveMessage,
    mobileSafetyConfirmOtherUserTemplate,
    mobileSendSafetyConfirmToOtherUser,
    mobileSendSafetyConfirmDetailToOtherUser,
    mobileSafetyConfirmRegister
  },
  // storeの情報はセッションストレージに持たせる
  plugins: [
    createPersistedState({
      key: process.env.VUE_APP_NAME,
      storage: window.sessionStorage
    })
  ]
});
export default store;

// #Topic: getModule()するとstore内のmoduleを探して返してくれる
// これをexportすることで、各vueはこの変数をimportするだけでstoreModuleを通常のクラスライクに使用できる
export const commonStore: common = getModule(common, store);
export const userStore: loginUser = getModule(loginUser, store);
export const fontsStore: fonts = getModule(fonts, store);
export const notificationStore: notification = getModule(notification, store);
export const tabIndexesStore: tabIndexes = getModule(tabIndexes, store);
export const systemSettingStore: systemSetting = getModule(systemSetting, store);
export const editAccountStore: editAccount = getModule(editAccount, store);
export const editOtherOfficeConnectionStore: editOtherOfficeConnection = getModule(editOtherOfficeConnection, store);
export const needRefreshesStore: needRefreshes = getModule(needRefreshes, store);
export const nationalHolidaysStore: nationalHolidays = getModule(nationalHolidays, store);
export const regionsStore: regions = getModule(regions, store);
export const screenInputTemporaryStore: screenInputTemporary = getModule(screenInputTemporary, store);
export const itemListFilterStore: itemListFilter = getModule(itemListFilter, store);

export const mobileMessageHistoryStore: mobileMessageHistory = getModule(mobileMessageHistory, store);
export const mobileMessageRegisterStore: mobileMessageRegister = getModule(mobileMessageRegister, store);
export const mobileMessageReplyDetailStore: mobileMessageReplyDetail = getModule(mobileMessageReplyDetail, store);
export const mobileFamilyReceiveMessageStore: mobileFamilyReceiveMessage = getModule(mobileFamilyReceiveMessage, store);
export const mobileFamilyReplyReceiveMessageStore: mobileFamilyReplyReceiveMessage = getModule(mobileFamilyReplyReceiveMessage, store);
export const mobileOtherUserReceiveMessageStore: mobileOtherUserReceiveMessage = getModule(mobileOtherUserReceiveMessage, store);
export const mobileOtherUserReplyReceiveMessageStore: mobileOtherUserReplyReceiveMessage = getModule(mobileOtherUserReplyReceiveMessage, store);
export const mobileSendSafetyConfirmToOtherUserStore: mobileSendSafetyConfirmToOtherUser = getModule(mobileSendSafetyConfirmToOtherUser, store);
export const mobileSendSafetyConfirmDetailToOtherUserStore: mobileSendSafetyConfirmDetailToOtherUser = getModule(mobileSendSafetyConfirmDetailToOtherUser, store);
export const mobileSafetyConfirmOtherUserTemplateStore: mobileSafetyConfirmOtherUserTemplate = getModule(mobileSafetyConfirmOtherUserTemplate, store);
export const mobileSafetyConfirmRegisterStore: mobileSafetyConfirmRegister = getModule(mobileSafetyConfirmRegister, store);
